import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClownNose, LiveBackground } from "./ClownNose";

const intereses = ["Podcast", "Utopistas", "Revista Cometa", "Tejer una utopía", "Colaborar / Alianzas", "Invitar a Araceli"];

export const LeadForm = () => {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", mensaje: "" });
  const [sel, setSel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const toggle = (i) => setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contacto", { ...form, mensaje: form.mensaje || "Quiero saber más de Cometa.", motivo: sel.join(", ") || "General", consentimiento: true, origen: "home-lead" });
      api.post("/newsletter", { email: form.email, nombre: form.nombre }).catch(() => {});
      setDone(true);
    } catch {
      toast.error("No pudimos enviar tu mensaje. Intenta de nuevo.");
    } finally { setLoading(false); }
  };

  const field = "w-full bg-white/90 border-2 border-transparent focus:border-cometa-dorado rounded-2xl px-5 py-4 text-cometa-tinta placeholder-cometa-tinta/40 focus:outline-none transition";

  return (
    <section className="relative overflow-hidden bg-cometa-marino text-cometa-crema" data-testid="lead-form-section">
      <LiveBackground tone="dark" />
      <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-2">
          <p className="font-script text-4xl md:text-5xl text-cometa-dorado mb-3">Conversemos</p>
          <h2 className="text-4xl md:text-6xl leading-[1] mb-6">Cuéntanos qué te trae <em className="text-cometa-celeste">a Cometa.</em></h2>
          <p className="text-cometa-crema/85 text-lg leading-relaxed mb-8">Elige lo que te interesa y te escribimos con lo que sí vale la pena: episodios, experiencias Utopistas, la Revista y formas de sumarte.</p>
          <ul className="space-y-3 text-cometa-crema/85">
            {["Respuesta personal, no automática", "Sin spam: escribimos cuando hay algo que compartir", "Puedes darte de baja cuando quieras"].map((t) => (
              <li key={t} className="flex items-center gap-3"><span className="h-6 w-6 rounded-full bg-cometa-dorado/20 text-cometa-dorado flex items-center justify-center"><Check size={14} /></span>{t}</li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3 relative">
          <ClownNose size={44} pulse className="absolute -top-5 -right-3 z-10 border-4 border-cometa-marino" />
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="ok" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-cometa-crema text-cometa-tinta rounded-[2rem] p-10 text-center" data-testid="lead-form-success">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }} className="mx-auto mb-5 h-20 w-20 rounded-full bg-cometa-dorado flex items-center justify-center"><Sparkles size={34} /></motion.div>
                <p className="font-script text-4xl text-cometa-marino mb-2">¡Gracias, {form.nombre.split(" ")[0]}!</p>
                <p className="text-lg text-cometa-tinta/80">Ya tenemos tu mensaje. Te escribimos pronto con algo que valga la pena.</p>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-cometa-crema/10 backdrop-blur-md border border-cometa-crema/15 rounded-[2rem] p-6 md:p-8 space-y-5" data-testid="lead-form">
                <div>
                  <p className="text-xs tracking-[0.3em] text-cometa-dorado mb-3">ME INTERESA</p>
                  <div className="flex flex-wrap gap-2">
                    {intereses.map((i) => (
                      <button type="button" key={i} onClick={() => toggle(i)} data-testid={`lead-chip-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${sel.includes(i) ? "bg-cometa-dorado text-cometa-tinta border-cometa-dorado scale-105" : "border-cometa-crema/30 hover:border-cometa-dorado hover:text-cometa-dorado"}`}>
                        {i}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Tu nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={field} data-testid="lead-nombre" />
                  <input required type="email" placeholder="tu@correo.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid="lead-email" />
                </div>
                <input placeholder="WhatsApp (opcional)" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className={field} data-testid="lead-telefono" />
                <textarea rows={3} placeholder="¿Cuál es tu utopía o qué te gustaría contarnos? (opcional)" value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} className={field} data-testid="lead-mensaje" />
                <button type="submit" disabled={loading} data-testid="lead-submit" className="w-full inline-flex items-center justify-center gap-2 bg-cometa-dorado text-cometa-tinta px-8 py-5 rounded-full text-lg font-bold hover:bg-cometa-carmesi hover:text-white transition disabled:opacity-60">
                  {loading ? "Enviando..." : <>Quiero saber más <Send size={18} /></>}
                </button>
                <p className="text-xs text-cometa-crema/50 text-center">Al enviar aceptas recibir la Carta desde la Cometa. Sin spam, prometido.</p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
